import React from "react";
import {
  Sheet,
  Typography,
  Select,
  Option,
  Button,
  FormControl,
  FormLabel,
} from "@mui/joy";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ListFilter as FilterList, Calendar } from "lucide-react";
import { format } from "date-fns";
import axios from "axios";
import Stack from "@mui/joy/Stack";

const FilterHeader = ({
  filterOption,
  onDateChange,
  onProjectChange,
  projects,
  setLeads,
}) => {
  const today = new Date();
  const isFilterActive = () => {
    return (
      filterOption.startDate !== null ||
      filterOption.endDate !== null ||
      filterOption.project !== 0
    );
  };
  const formatDateStr = (date) => {
    if (!date) return "";
    return format(date, "MMM dd, yyyy");
  };
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const handleDateChange = (type, newDate) => {
    onDateChange({
      ...filterOption,
      [type]: newDate,
    });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      startDate: filterOption.startDate,
      endDate: filterOption.endDate,
      project_ids: filterOption.project === 0 ? 0 : filterOption.project,
    };

    try {
      const response = await axios.post(
        `${BASE_URL}/leads/user/filteredleads`,
        payload,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      setLeads(response.data);
    } catch (error) {
      console.error("Error fetching leads:", error);
    }
  };

  return (
    <Sheet
      variant="outlined"
      sx={{
        borderRadius: "xl",
        p: 3,
        mb: 3,
        backgroundColor: "white",
        boxShadow: "sm",
      }}
    >
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            <Typography
              level="h3"
              sx={{
                background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              Leads Management
            </Typography>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <FormControl>
                <FormLabel>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Calendar size={18} />
                    Start Date
                  </div>
                </FormLabel>
                <DatePicker
                  value={filterOption.startDate}
                  onChange={(newValue) =>
                    handleDateChange("startDate", newValue)
                  }
                  maxDate={filterOption.endDate || today}
                  slotProps={{
                    textField: {
                      size: "sm",
                      sx: {
                        "--Input-focusedThickness": "2px",
                        "--Input-focusedHighlight": "rgb(59 130 246)",
                      },
                    },
                  }}
                />
              </FormControl>

              <FormControl>
                <FormLabel>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Calendar size={18} />
                    End Date
                  </div>
                </FormLabel>
                <DatePicker
                  value={filterOption.endDate}
                  onChange={(newValue) => handleDateChange("endDate", newValue)}
                  maxDate={today}
                  minDate={filterOption.startDate || undefined}
                  slotProps={{
                    textField: {
                      size: "sm",
                      sx: {
                        "--Input-focusedThickness": "2px",
                        "--Input-focusedHighlight": "rgb(59 130 246)",
                      },
                    },
                  }}
                />
              </FormControl>

              <FormControl>
                <FormLabel>
                  <div className="flex items-center gap-2 text-gray-700">
                    <FilterList size={18} />
                    Project
                  </div>
                </FormLabel>
                <Stack
                  spacing={2}
                  className="mt-3"
                  sx={{ alignItems: "flex-start" }}
                >
                  <Select
                    value={filterOption.project}
                    placeholder="Select project"
                    multiple
                    onChange={(_, value) => onProjectChange(value)}
                    sx={{
                      "--Select-focusedThickness": "2px",
                      "--Select-focusedHighlight": "rgb(59 130 246)",
                      width: "100%",
                    }}
                  >
                    {projects.map((project) => (
                      <Option key={project.id} value={project.id}>
                        {project.name}
                      </Option>
                    ))}
                  </Select>
                </Stack>
              </FormControl>
            </LocalizationProvider>
          </div>

          {isFilterActive() && (
            <Typography level="body-sm" className="text-gray-600">
              Active Filters:{" "}
              {[
                filterOption.startDate &&
                  `From ${formatDateStr(filterOption.startDate)}`,
                filterOption.endDate &&
                  `To ${formatDateStr(filterOption.endDate)}`,
                filterOption.project !== 0 &&
                  `Project: ${
                    projects.find((p) => p.id === filterOption.project)?.name
                  }`,
              ]
                .filter(Boolean)
                .join(" • ")}
            </Typography>
          )}
          <div className="col-end-4">
            <Button
              type="submit"
              className="flex align-baseline"
              startDecorator={<FilterList size={18} />}
              sx={{
                background: isFilterActive()
                  ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                  : undefined,
                "&:hover": {
                  background: isFilterActive()
                    ? "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)"
                    : undefined,
                },
              }}
              variant={isFilterActive() ? "solid" : "outlined"}
              color={isFilterActive() ? "primary" : "neutral"}
            >
              {isFilterActive() ? "Apply Filters" : "No Filters"}
            </Button>
          </div>
        </div>
      </form>
    </Sheet>
  );
};

export default FilterHeader;
